import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, Clock, Building2, Timer, Home, Briefcase, Sparkles, BedDouble,
  ClipboardList, Users, CalendarCheck, Smile, Check, ArrowRight, Zap, Wallet,
  Headphones, TrendingUp, MapPin,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FaqSection from "@/components/site/FaqSection";
import SocialProof from "@/components/site/SocialProof";
import { CleanerForm, ContactForm } from "@/components/site/LeadForms";
import { useLang, waLink } from "@/i18n";
import { useWizard } from "@/seo/WizardContext";
import Seo from "@/seo/Seo";
import { localBusinessLd, faqLd } from "@/seo/schema";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_cleaner-connect-30/artifacts/3wa4dk27_wmremove-transformed.jpeg";
const CLEANER_IMG = "https://images.unsplash.com/photo-1680728334131-220313e30370?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const SERVICE_ICONS = [Home, Briefcase, Sparkles, BedDouble];
const STEP_ICONS = [ClipboardList, Users, CalendarCheck, Smile];
const TRUST_ICONS = [ShieldCheck, Clock, Building2, Timer];
const WHY_ICONS = [Zap, ShieldCheck, CalendarCheck, Check, Headphones];
const BENEFIT_ICONS = [Clock, MapPin, Wallet, Headphones, TrendingUp];

const WHY_CARD_ICONS = [ShieldCheck, Users, CalendarCheck, Wallet];

export default function Landing() {
  const { t } = useLang();
  const { openWizard } = useWizard();

  const openRequest = openWizard;
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="MyCleaner | Betrouwbare schoonmakers voor woning & bedrijf"
        description="MyCleaner regelt eigen, zorgvuldig gescreende schoonmakers voor particulieren en bedrijven in de regio Amsterdam, Haarlem en Hoofddorp. Plan eenvoudig uw schoonmaak en vraag vrijblijvend een offerte aan."
        path="/"
        jsonLd={[
          localBusinessLd(),
          faqLd([
            { q: "Moet ik zelf schoonmaakmiddelen en materialen regelen?", a: "Nee. Onze schoonmakers nemen alle benodigde schoonmaakmiddelen mee. We vragen u alleen een stofzuiger en dweil beschikbaar te stellen." },
            { q: "Kan ik een afspraak wijzigen of annuleren?", a: "Ja. U kunt kosteloos wijzigen of annuleren, mits u dit minimaal 24 uur van tevoren doorgeeft." },
            { q: "Zijn jullie schoonmakers gescreend?", a: "Ja. Al onze schoonmakers worden zorgvuldig geselecteerd, gescreend en gecontroleerd." },
            { q: "Hoe betaal ik?", a: "Na de schoonmaak ontvangt u een factuur. Betaling verloopt via de vooraf afgesproken betaalmethode." },
          ]),
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 to-white px-5 pb-16 pt-32 md:px-8 md:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal delay={0.05}>
              <h1 className="font-heading text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                {t.hero.title}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">{t.hero.subtitle}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  data-testid="hero-request-btn"
                  onClick={openRequest}
                  className="rounded-full bg-blue-600 px-8 py-6 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  {t.hero.cta1} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <button
                  data-testid="hero-info-btn"
                  onClick={() => scrollTo("how")}
                  className="inline-flex items-center justify-center rounded-full border-2 border-slate-200 px-8 py-3.5 text-base font-semibold text-slate-800 transition-all hover:border-blue-600 hover:text-blue-700"
                >
                  {t.hero.cta2}
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 grid gap-3 sm:grid-cols-3">
                {t.hero.usps.map((u, i) => (
                  <div key={i} data-testid={`hero-usp-${i}`} className="flex items-center gap-2.5 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium text-slate-700">{u}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative">
              <img
                src={HERO_IMG}
                alt="Professionele MyCleaner schoonmaker in een schone, lichte woning"
                className="aspect-[4/3] w-full rounded-3xl object-cover object-center shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white p-5 shadow-xl sm:block">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-heading text-lg font-bold text-slate-900">100%</p>
                    <p className="text-xs text-slate-500">{t.hero.stat1}</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-slate-50 px-5 py-24 md:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t.services.title}</h2>
            <p className="mt-4 text-lg text-slate-600">{t.services.subtitle}</p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.services.items.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div
                    data-testid={`service-card-${i}`}
                    className="group flex h-full flex-col rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white transition-transform group-hover:scale-105">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-semibold text-slate-900">{s.t}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{s.d}</p>
                    <button
                      data-testid={`service-request-${i}`}
                      onClick={openRequest}
                      className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:gap-2"
                    >
                      {t.nav.cta} <ArrowRight className="h-4 w-4 transition-all" />
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-blue-50/40 px-5 py-24 md:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t.pricing.title}</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{t.pricing.subtitle}</p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.pricing.items.map((p, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div
                    data-testid={`pricing-card-${i}`}
                    className="flex h-full flex-col rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{p.t}</h3>
                    <div className="mt-4 flex items-end gap-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t.pricing.from}</span>
                      <span className="font-heading text-3xl font-bold text-blue-700">{p.p}</span>
                      <span className="pb-0.5 text-sm text-slate-500">{t.pricing.unit}</span>
                    </div>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-500">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Why choose - benefits row */}
          <Reveal className="mt-16">
            <h3 className="text-center font-heading text-2xl font-semibold text-slate-900">{t.pricing.benefitsTitle}</h3>
            <div className="mx-auto mt-8 grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {t.pricing.benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium text-slate-700">{b}</span>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Quote CTA */}
          <Reveal className="mt-14">
            <div className="relative overflow-hidden rounded-[2rem] bg-blue-600 px-6 py-12 text-center md:px-16">
              <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-blue-500/40" />
              <div className="relative">
                <h3 className="font-heading text-2xl font-bold text-white sm:text-3xl">{t.pricing.ctaTitle}</h3>
                <p className="mx-auto mt-3 max-w-xl text-blue-50">{t.pricing.ctaText}</p>
                <Button
                  data-testid="pricing-quote-btn"
                  onClick={openRequest}
                  className="mt-7 rounded-full bg-white px-8 py-6 text-base font-semibold text-blue-700 hover:bg-blue-50"
                >
                  {t.pricing.ctaTitle} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY US - 4 cards */}
      <section className="px-5 py-24 md:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t.why.title}</h2>
            <p className="mt-4 text-lg text-slate-600">{t.why.subtitle}</p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.why.cards.map((c, i) => {
              const Icon = WHY_CARD_ICONS[i] || Check;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="flex h-full flex-col rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-semibold text-slate-900">{c.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{c.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-12 text-center">
            <Button
              data-testid="why-request-btn"
              onClick={openRequest}
              className="rounded-full bg-blue-600 px-8 py-6 text-base font-semibold text-white hover:bg-blue-700"
            >
              {t.nav.cta} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <SocialProof />

      {/* HOW IT WORKS */}
      <section id="how" className="px-5 py-24 md:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t.how.title}</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-4">
            {t.how.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="relative text-center md:text-left">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 md:mx-0">
                      <Icon className="h-8 w-8" />
                    </div>
                    <span className="mt-5 block font-heading text-5xl font-bold text-blue-100">0{i + 1}</span>
                    <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{s.t}</h3>
                    <p className="mt-1 text-sm text-slate-500">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-blue-50/50 px-5 py-24 md:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-700 shadow-sm">
            {t.about.title}
          </span>
          <p className="mt-6 font-heading text-2xl font-medium leading-relaxed text-slate-800 sm:text-3xl">
            {t.about.text}
          </p>
        </Reveal>
      </section>

      {/* BECOME A CLEANER */}
      <section id="become-cleaner" className="px-5 py-24 md:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-2">
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-700">
                  {t.become.badge}
                </span>
                <h2 className="mt-6 font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t.become.title}</h2>
                <p className="mt-4 text-lg text-slate-600">{t.become.text}</p>

                <img src={CLEANER_IMG} loading="lazy" decoding="async" alt="MyCleaner schoonmaker aan het werk in een woning" className="mt-8 aspect-[16/10] w-full rounded-3xl object-cover shadow-lg" />

                <h3 className="mt-10 font-heading text-xl font-semibold text-slate-900">{t.become.benefitsTitle}</h3>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {t.become.benefits.map((b, i) => {
                    const Icon = BENEFIT_ICONS[i] || Check;
                    return (
                      <li key={i} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                        <Icon className="h-5 w-5 flex-shrink-0 text-blue-600" />
                        <span className="text-sm font-medium text-slate-700">{b}</span>
                      </li>
                    );
                  })}
                </ul>

                <h3 className="mt-10 font-heading text-xl font-semibold text-slate-900">{t.become.howTitle}</h3>
                <div className="mt-4 space-y-3">
                  {t.become.how.map((h, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 font-heading text-sm font-bold text-white">
                        {i + 1}
                      </span>
                      <span className="text-base text-slate-700">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="lg:sticky lg:top-28">
                <CleanerForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 py-16 md:px-8">
        <Reveal className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-blue-600 px-6 py-16 text-center md:px-16 md:py-20">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-blue-500/40" />
            <div className="absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-blue-700/40" />
            <div className="relative">
              <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">{t.finalCta.title}</h2>
              <p className="mx-auto mt-4 max-w-xl text-lg text-blue-50">{t.finalCta.subtitle}</p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Button
                  data-testid="final-request-btn"
                  onClick={openRequest}
                  className="rounded-full bg-white px-8 py-6 text-base font-semibold text-blue-700 hover:bg-blue-50"
                >
                  {t.finalCta.cta1}
                </Button>
                <a
                  data-testid="final-whatsapp-btn"
                  href={waLink("Hoi MyCleaner, ik wil direct een schoonmaker regelen.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full border-2 border-white/70 px-8 py-3.5 text-base font-semibold text-white hover:bg-white/10"
                >
                  {t.finalCta.cta2}
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-slate-50 px-5 py-24 md:px-8">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-2">
          <Reveal>
            <div>
              <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{t.contact.title}</h2>
              <p className="mt-4 text-lg text-slate-600">{t.contact.subtitle}</p>
              <a
                data-testid="contact-whatsapp-btn"
                href={waLink("Hoi MyCleaner!")}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-base font-semibold text-white shadow-lg hover:brightness-105"
              >
                {t.contact.cta}
              </a>
              <div className="mt-8 space-y-3 text-slate-600">
                <p className="flex items-center gap-2"><span className="font-semibold text-slate-800">Tel:</span> +31 6 59 12 85 21</p>
                <p className="flex items-center gap-2"><span className="font-semibold text-slate-800">E-mail:</span> info@mycleaner.nl</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      <Footer />
    </div>
  );
}
